/**
 * Admin Review Moderation
 * 
 * Features:
 * 1. View all reviews with filters (pending, published, hidden)
 * 2. Multi-dimensional ratings display
 * 3. Moderation actions (approve, hide, delete)
 * 4. View flagged words and report reasons
 * 5. Vendor response management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Star,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Trash2,
  MessageSquare,
  Flag,
  Search,
  RefreshCw,
  ShieldCheck,
  Image as ImageIcon,
  Package,
  User,
  Calendar,
  ThumbsUp,
  Filter
} from 'lucide-react';

// Star Rating Display Component
const StarRating = ({ rating, size = 'sm' }) => {
  const sizeClass = size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${
            star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
          }`}
        />
      ))}
      <span className="ml-1 text-sm font-medium">{rating}/5</span>
    </div>
  );
};

// Multi-dimensional Rating Display
const RatingBreakdown = ({ review }) => {
  const ratings = [
    { label: 'Overall', value: review.rating_overall },
    { label: 'Quality', value: review.rating_quality },
    { label: 'Value', value: review.rating_value },
    { label: 'Delivery', value: review.rating_delivery },
  ];

  return (
    <div className="grid grid-cols-2 gap-2">
      {ratings.map((r) => (
        r.value && (
          <div key={r.label} className="flex items-center justify-between text-sm">
            <span className="text-gray-500">{r.label}:</span>
            <div className="flex items-center gap-1">
              <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
              <span>{r.value}</span>
            </div>
          </div>
        )
      ))}
    </div>
  );
};

// Moderation Status Badge
const ModerationBadge = ({ status }) => {
  const variants = {
    published: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
    pending_review: { color: 'bg-amber-100 text-amber-800', icon: AlertTriangle },
    hidden: { color: 'bg-red-100 text-red-800', icon: EyeOff },
  };

  const config = variants[status] || variants.pending_review;
  const Icon = config.icon;

  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {status === 'pending_review' ? 'Pending Review' : status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

// Review Card Component
const ReviewCard = ({ review, onModerate, onViewDetails }) => {
  return (
    <Card className={`${review.is_auto_flagged ? 'border-amber-300 border-2' : ''}`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-lg font-semibold text-gray-600">
                {review.customer_name?.[0]?.toUpperCase() || 'A'}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{review.customer_name || 'Anonymous'}</span>
                {review.is_verified_purchase && (
                  <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-500">{review.customer_email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ModerationBadge status={review.moderation_status} />
            {review.report_count > 0 && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <Flag className="h-3 w-3" />
                {review.report_count} report{review.report_count > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>

        {/* Rating */}
        <div className="mb-3">
          <StarRating rating={review.rating_overall} size="lg" />
        </div>

        {/* Review Content */}
        {review.review_title && (
          <h4 className="font-semibold mb-1">{review.review_title}</h4>
        )}
        {review.review_text && (
          <p className="text-gray-700 text-sm mb-3 line-clamp-3">{review.review_text}</p>
        )}

        {/* Images */}
        {review.review_images?.length > 0 && (
          <div className="flex gap-2 mb-3">
            {review.review_images.map((img, idx) => (
              <img
                key={img.id || img.name || `${img.title || "img"}-${idx}`}
                src={img}
                alt={`Review image ${idx + 1}`}
                className="w-16 h-16 object-cover rounded-lg border"
              />
            ))}
          </div>
        )}

        {/* Flagged Words Warning */}
        {review.flagged_words?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
            <div className="flex items-center gap-2 text-amber-700 text-sm">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-medium">Flagged words detected:</span>
            </div>
            <div className="flex flex-wrap gap-1 mt-1">
              {review.flagged_words.map((word, idx) => (
                <Badge key={word.id || word.name || `${word.title || "word"}-${idx}`} variant="outline" className="bg-amber-100 text-amber-800 text-xs">
                  {word}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Listing Info */}
        {review.listing_title && (
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
            <Package className="h-4 w-4" />
            <span>{review.listing_title}</span>
            {review.listing_type && (
              <Badge variant="secondary" className="text-xs">{review.listing_type}</Badge>
            )}
          </div>
        )}

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 border-t pt-3">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {new Date(review.submission_date).toLocaleDateString()}
          </span>
          {review.order_id && (
            <span className="flex items-center gap-1">
              <Package className="h-3 w-3" />
              Order: {review.order_id.slice(0, 8)}...
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t">
          {review.moderation_status !== 'published' && (
            <Button
              size="sm"
              variant="default"
              className="bg-green-600 hover:bg-green-700"
              onClick={() => onModerate(review.review_id, 'approve')}
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Approve
            </Button>
          )}
          {review.moderation_status !== 'hidden' && (
            <Button
              size="sm"
              variant="outline"
              className="text-amber-600 border-amber-300"
              onClick={() => onModerate(review.review_id, 'hide')}
            >
              <EyeOff className="h-4 w-4 mr-1" />
              Hide
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 border-red-300"
            onClick={() => onModerate(review.review_id, 'delete')}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onViewDetails(review)}
          >
            <Eye className="h-4 w-4 mr-1" />
            Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Component
const AdminReviewModeration = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReview, setSelectedReview] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [moderationModalOpen, setModerationModalOpen] = useState(false);
  const [moderationAction, setModerationAction] = useState(null);
  const [moderationReason, setModerationReason] = useState('');
  const [stats, setStats] = useState({ pending: 0, published: 0, hidden: 0 });
  
  const { apiCall } = useAuth();
  const { toast } = useToast();

  // Fetch reviews
  const fetchReviews = useCallback(async (statusFilter) => {
    setLoading(true);
    try {
      const endpoint = statusFilter === 'pending' 
        ? '/api/reviews/admin/pending'
        : `/api/reviews/admin/all?status_filter=${statusFilter}`;
      
      const data = await apiCall(endpoint);
      
      if (Array.isArray(data)) {
        setReviews(data);
      } else if (data?.reviews) {
        setReviews(data.reviews);
      } else {
        setReviews([]);
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      toast({
        title: 'Error',
        description: 'Failed to load reviews',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [apiCall, toast]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const [pending, published, hidden] = await Promise.all([
        apiCall('/api/reviews/admin/pending'),
        apiCall('/api/reviews/admin/all?status_filter=published&limit=1'),
        apiCall('/api/reviews/admin/all?status_filter=hidden&limit=1')
      ]);
      
      setStats({
        pending: Array.isArray(pending) ? pending.length : 0,
        published: published?.total || 0,
        hidden: hidden?.total || 0
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [apiCall]);

  useEffect(() => {
    const statusMap = {
      pending: 'pending_review',
      published: 'published',
      hidden: 'hidden'
    };
    fetchReviews(statusMap[activeTab] || 'pending_review');
    fetchStats();
  }, [activeTab, fetchReviews, fetchStats]);

  // Handle moderation action
  const handleModerate = async (reviewId, action) => {
    setSelectedReview(reviews.find(r => r.review_id === reviewId));
    setModerationAction(action);
    
    if (action === 'approve') {
      // Approve immediately
      await executeModeration(reviewId, action, '');
    } else {
      // Show modal for reason
      setModerationModalOpen(true);
    }
  };

  const executeModeration = async (reviewId, action, reason) => {
    try {
      await apiCall(`/api/reviews/admin/${reviewId}/moderate?action=${action}${reason ? `&reason=${encodeURIComponent(reason)}` : ''}`, {
        method: 'PUT'
      });
      
      toast({
        title: 'Success',
        description: `Review ${action}d successfully`
      });
      
      // Refresh list
      const statusMap = {
        pending: 'pending_review',
        published: 'published',
        hidden: 'hidden'
      };
      fetchReviews(statusMap[activeTab] || 'pending_review');
      fetchStats();
      setModerationModalOpen(false);
      setModerationReason('');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to moderate review',
        variant: 'destructive'
      });
    }
  };

  // Filter reviews by search
  const filteredReviews = reviews.filter(review => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      review.customer_name?.toLowerCase().includes(search) ||
      review.customer_email?.toLowerCase().includes(search) ||
      review.review_title?.toLowerCase().includes(search) ||
      review.review_text?.toLowerCase().includes(search) ||
      review.listing_title?.toLowerCase().includes(search)
    );
  });

  if (loading && reviews.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold">Review Moderation</h1>
            <p className="text-gray-500 text-sm md:text-base">Manage customer reviews and ratings</p>
          </div>
          <Button variant="outline" onClick={() => {
            const statusMap = { pending: 'pending_review', published: 'published', hidden: 'hidden' };
            fetchReviews(statusMap[activeTab]);
            fetchStats();
          }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6">
          <Card className="cursor-pointer hover:bg-amber-50" onClick={() => setActiveTab('pending')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.pending}</div>
                  <div className="text-sm text-gray-500">Pending</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:bg-green-50" onClick={() => setActiveTab('published')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.published}</div>
                  <div className="text-sm text-gray-500">Published</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:bg-red-50" onClick={() => setActiveTab('hidden')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <EyeOff className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.hidden}</div>
                  <div className="text-sm text-gray-500">Hidden</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search reviews..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              <span className="hidden sm:inline">Pending</span>
            </TabsTrigger>
            <TabsTrigger value="published" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Published</span>
            </TabsTrigger>
            <TabsTrigger value="hidden" className="flex items-center gap-2">
              <EyeOff className="h-4 w-4" />
              <span className="hidden sm:inline">Hidden</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {filteredReviews.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Star className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No {activeTab} reviews found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredReviews.map((review) => (
                  <ReviewCard
                    key={review.review_id}
                    review={review}
                    onModerate={handleModerate}
                    onViewDetails={(r) => {
                      setSelectedReview(r);
                      setDetailsModalOpen(true);
                    }}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Details Modal */}
        <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Review Details</DialogTitle>
            </DialogHeader>
            {selectedReview && (
              <div className="space-y-4">
                {/* Customer Info */}
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-xl font-semibold text-gray-600">
                      {selectedReview.customer_name?.[0]?.toUpperCase() || 'A'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{selectedReview.customer_name || 'Anonymous'}</p>
                    <p className="text-sm text-gray-500">{selectedReview.customer_email}</p>
                  </div>
                </div>

                {/* Ratings Breakdown */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Rating Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RatingBreakdown review={selectedReview} />
                  </CardContent>
                </Card>

                {/* Review Content */}
                <div>
                  <h4 className="font-semibold text-lg">{selectedReview.review_title}</h4>
                  <p className="text-gray-700 mt-2">{selectedReview.review_text}</p>
                </div>

                {/* Images */}
                {selectedReview.review_images?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Images</p>
                    <div className="flex gap-2">
                      {selectedReview.review_images.map((img, idx) => (
                        <img
                          key={img.id || img.name || `${img.title || "img"}-${idx}`}
                          src={img}
                          alt={`Review image ${idx + 1}`}
                          className="w-24 h-24 object-cover rounded-lg border cursor-pointer"
                          onClick={() => window.open(img, '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Reports */}
                {selectedReview.report_count > 0 && (
                  <Card className="border-red-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-red-600 flex items-center gap-2">
                        <Flag className="h-4 w-4" />
                        Reports ({selectedReview.report_count})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-500">
                        This review has been reported by users. Please review carefully.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Meta */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Order ID:</span>
                    <p className="font-mono">{selectedReview.order_id || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Submitted:</span>
                    <p>{new Date(selectedReview.submission_date).toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Verified Purchase:</span>
                    <p>{selectedReview.is_verified_purchase ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Moderation Status:</span>
                    <ModerationBadge status={selectedReview.moderation_status} />
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Moderation Reason Modal */}
        <Dialog open={moderationModalOpen} onOpenChange={setModerationModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {moderationAction === 'hide' ? 'Hide Review' : 'Delete Review'}
              </DialogTitle>
              <DialogDescription>
                Please provide a reason for this action.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              placeholder="Enter reason..."
              value={moderationReason}
              onChange={(e) => setModerationReason(e.target.value)}
              rows={3}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setModerationModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant={moderationAction === 'delete' ? 'destructive' : 'default'}
                onClick={() => executeModeration(selectedReview?.review_id, moderationAction, moderationReason)}
              >
                Confirm {moderationAction === 'hide' ? 'Hide' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminReviewModeration;
